import { RiFlagLine } from '@remixicon/react';

import { ButtonIcon, IconExternalLink } from '@spotify-internal/encore-web';
import {
  DialogCompositeMui as DialogComposite,
  extractLastNameComponent,
  pathToModuleStyle,
  useDialog,
} from '@spotify/confidence-core-react';
import { AttachmentFragment } from '@spotify/confidence-graphql';
import {
  ArtifactCardBase,
  DefaultArtifactListItem,
  ResolvedTitleProvider,
  createArtifactExtension,
  getArtifactNavigationPath,
  isExternalLink,
  useResolvedTitle,
} from '@spotify/plugin-confidence-projects-react';

import { projectsPlugin } from '../plugin';
import { ResourcePreview } from './previews/ResourcePreview';
import {
  RuleArtifactCard,
  RuleArtifactListItem,
  SegmentArtifactCard,
  SegmentArtifactListItem,
  WorkflowArtifactCard,
  WorkflowArtifactListItem,
} from './resolvers';
import { isRuleUri, isSegmentUri, isWorkflowInstanceUri } from './resourceUri';

import styles from './ResourceArtifactExtension.module.css';

const GoToResourceButton = ({ artifact }: { artifact: AttachmentFragment }) => {
  const navigationPath = getArtifactNavigationPath(artifact.uri);

  if (!navigationPath) {
    return null;
  }

  return (
    <ButtonIcon
      variant="tertiary"
      onClick={() => window.open(`/${navigationPath}`, '_blank')}
      aria-label="Go to resource"
    >
      <IconExternalLink />
    </ButtonIcon>
  );
};

const ResourcePreviewDialogTitle = ({
  artifact,
}: {
  artifact: AttachmentFragment;
}) => {
  const resolvedTitle = useResolvedTitle();
  const displayName =
    resolvedTitle ||
    artifact.displayName ||
    extractLastNameComponent(artifact.uri);

  return <DialogComposite.Title>{displayName}</DialogComposite.Title>;
};

const ResourcePreviewDialogContent = ({
  artifact,
  onClose,
}: {
  artifact: AttachmentFragment;
  onClose: () => void;
}) => (
  <DialogComposite.Root static onClose={onClose}>
    <DialogComposite.Content>
      <DialogComposite.CloseButton />
      <DialogComposite.Header>
        <ResourcePreviewDialogTitle artifact={artifact} />
      </DialogComposite.Header>
      <DialogComposite.Body className={styles.dialogBody}>
        <div className={styles.dialogActions}>
          <GoToResourceButton artifact={artifact} />
        </div>
        <ResourcePreview artifact={artifact} />
      </DialogComposite.Body>
    </DialogComposite.Content>
  </DialogComposite.Root>
);

const useOpenResourcePreviewDialog = (artifact: AttachmentFragment) => {
  const { openDialog, closeDialog } = useDialog();

  return () => {
    openDialog({
      content: (
        <ResolvedTitleProvider>
          <ResourcePreviewDialogContent
            artifact={artifact}
            onClose={closeDialog}
          />
        </ResolvedTitleProvider>
      ),
      dialogProps: { maxWidth: 'sm', fullWidth: true },
    });
  };
};

const ResourceArtifactCard = ({
  artifact,
  onDelete,
  renderCard,
}: {
  artifact: AttachmentFragment;
  onDelete?: () => void;
  renderCard?: (props: {
    artifact: AttachmentFragment;
    onNavigate: () => void;
    onDelete?: () => void;
  }) => JSX.Element;
}) => {
  const openPreviewDialog = useOpenResourcePreviewDialog(artifact);

  if (renderCard) {
    return renderCard({
      artifact,
      onNavigate: openPreviewDialog,
      onDelete,
    });
  }

  return (
    <ArtifactCardBase
      artifact={artifact}
      onNavigate={openPreviewDialog}
      onDelete={onDelete}
    />
  );
};

export const ResourceArtifactExtension = projectsPlugin.provide(
  createArtifactExtension({
    id: 'resource',
    predicate: artifact => !isExternalLink(artifact.uri),
    icon: artifact => {
      const style = pathToModuleStyle(artifact.uri);
      if (style) {
        return {
          Icon: style.Icon,
          color: style.color,
        };
      }
      return { Icon: RiFlagLine, color: '#E74C3C' };
    },
    label: 'Resource',
    category: 'Resources',
    getNavigationPath: artifact => getArtifactNavigationPath(artifact.uri),
    render: artifact => <ResourcePreview artifact={artifact} />,
    renderHeaderActions: artifact => <GoToResourceButton artifact={artifact} />,
    renderListItem: (
      artifact,
      { onSelect, onNavigateToResource, onDelete, icon },
    ) => {
      if (isWorkflowInstanceUri(artifact.uri)) {
        return (
          <WorkflowArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      if (isSegmentUri(artifact.uri)) {
        return (
          <SegmentArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      if (isRuleUri(artifact.uri)) {
        return (
          <RuleArtifactListItem
            artifact={artifact}
            icon={icon}
            onSelect={onSelect}
            onNavigateToResource={onNavigateToResource}
            onDelete={onDelete}
          />
        );
      }
      const navigationPath = getArtifactNavigationPath(artifact.uri);
      const href = navigationPath ? `/${navigationPath}` : undefined;
      return (
        <DefaultArtifactListItem
          artifact={artifact}
          icon={icon}
          href={href}
          onSelect={onSelect}
          onDelete={onDelete}
        />
      );
    },
    renderCard: (artifact, { onDelete }) => {
      if (isWorkflowInstanceUri(artifact.uri)) {
        return (
          <ResourceArtifactCard
            artifact={artifact}
            onDelete={onDelete}
            renderCard={props => <WorkflowArtifactCard {...props} />}
          />
        );
      }
      if (isSegmentUri(artifact.uri)) {
        return (
          <ResourceArtifactCard
            artifact={artifact}
            onDelete={onDelete}
            renderCard={props => <SegmentArtifactCard {...props} />}
          />
        );
      }
      if (isRuleUri(artifact.uri)) {
        return (
          <ResourceArtifactCard
            artifact={artifact}
            onDelete={onDelete}
            renderCard={props => <RuleArtifactCard {...props} />}
          />
        );
      }
      return <ResourceArtifactCard artifact={artifact} onDelete={onDelete} />;
    },
  }),
);
